
import { Component, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { PERMISSION } from '@/permissions';
import TextField from '@/components/shared/TextField.vue';

const namespace: string = 'auth';

@Component({
	components: {
		TextField
	}
})
export default class LocationSelector extends Vue {
	@Action('toggleMobileNav', { namespace: 'ui' }) private toggleMobileNav!: (isOpen: boolean) => void;
	@Action('buildLocalMenu', { namespace: 'menus' }) private buildLocalMenu!: (restaurantId: string) => Promise<void>;
	@Action('setTenant', { namespace }) private setTenant!: (tenantId: string) => Promise<void>;
	@Action('setSwitchingLocation', { namespace }) private setSwitchingLocation!: (switchingLocation: boolean) => void;
	@Action('fetchRestaurant', { namespace }) private fetchRestaurant!: (payload: any) => Promise<void>;
	@Getter('getTenantId', { namespace }) private tenantId!: string;
	@Getter('getRestaurants', { namespace }) private restaurants!: Tenant[];
	@Getter('getRestaurant', { namespace }) private restaurant!: Restaurant;
	@Getter('hasPermission', { namespace }) private hasPermission!: (permission: PERMISSION) => boolean;

	private PERMISSION = PERMISSION;
	private selectorOpen: boolean = false;
	private searchString: string = '';
	private recentLocations: Tenant[] = [];

	private get currentEnv(): string {
		return process.env.NODE_ENV;
	}

	/**
	 * Filter restaurants based on search string and limit to 5 results.
	 * If no recent locations are found recentLocations will be an empty array,
	 * the dropdown will show first 5 locations in alphabetical order.
	 *
	 * @return {Tenant[]}
	 */
	private get restaurantList(): Tenant[] {
		// Null until /restaurants/list resolves — the drawer renders before that on a hard refresh.
		if (!this.restaurants) {
			return [];
		}
		if (this.searchString) {
			const lowercaseSearchString = this.searchString.toLowerCase();
			return this.restaurants.filter(restaurant => {
				return restaurant.name.toLowerCase().includes(lowercaseSearchString) && restaurant.id !== this.tenantId;

			}).slice(0, 5);
		}
		else if (!this.searchString && this.restaurants.length > 5) {
			const restaurantList: Tenant[] = [...this.recentLocations];
			let i = 0;
			// Add restaurants to the list until it reaches 5 or runs out of restaurants
			// Exclude the current tenant and any recent locations already in the list
			while (restaurantList.length < 5 && i < this.restaurants.length) {
				const restaurant = this.restaurants[i];
				if (restaurant.id !== this.tenantId && !this.recentLocations.some(loc => loc.id === restaurant.id)) {
					restaurantList.push(restaurant);
				}
				i++;
			}
			return restaurantList;
		}
		return this.restaurants;
	}

	private get currentTenant(): Tenant | null {
		return this.restaurants.find(restaurant => restaurant.id === this.tenantId) || null;
	}

	private created(): void {
		this.getRecentLocations();
	}

	/**
	 * Retrieve recent locations from session storage.
	 *
	 * @return {void}
	 */
	private getRecentLocations(): void {
		const recentLocations = sessionStorage.getItem('recentLocations');
		if (recentLocations) {
			this.recentLocations = JSON.parse(recentLocations);
		}
	}

	/**
	 * Update recent location list, set restaurant and redirect to default route when selected from the dropdown.
	 * * The switching-location flag unmounts the main router-view for the whole switch, so the current page
	 * * can't fire requests against the new tenant before its restaurant data and the redirect are in place.
	 *
	 * @param {Tenant} tenant
	 * @return {Promise<void>}
	 */
	private async setRestaurant(tenant: Tenant): Promise<void> {
		this.updateRecentLocations();
		this.closeNavAndSelector();
		this.setSwitchingLocation(true);
		try {
			await this.setTenant(tenant.id);
			await this.fetchRestaurant({ tenantId: tenant.id });
			if (this.restaurant.dashboard && !this.restaurant.k12) {
				await this.$router.replace('/dashboard').catch(() => {});
			}
			else if (this.restaurant.order_management) {
				await this.$router.replace('/order-management').catch(() => {});
			}
			else {
				await this.$router.replace('/menus').catch(() => {});
			}
		}
		finally {
			this.setSwitchingLocation(false);
		}
	}

	/**
	 * Add the current tenant to the recent locations and update sessionStorage.
	 * This happens before switching tenants to ensure the previous location is at the top of the list.
	 *
	 * @return {void}
	 */
	private updateRecentLocations(): void {
		if (!this.currentTenant) {
			return;
		}
		// Remove the tenant if it already exists in recent locations
		this.recentLocations = this.recentLocations.filter(loc => loc.id !== this.tenantId);

		// Add the new tenant to the beginning of the list
		this.recentLocations.unshift(this.currentTenant);

		// Limit to 5 recent locations
		if (this.recentLocations.length > 5) {
			this.recentLocations.pop();
		}
		sessionStorage.setItem('recentLocations', JSON.stringify(this.recentLocations));
	}

	/**
	 * Redirects to the locations page when "View all locations" is clicked.
	 *
	 * @return {void}
	 */
	private redirectToLocations(): void {
		this.closeNavAndSelector();
		this.$router.push('/locations').catch(() => {});
	}

	/**
	 * Redirects to the multi-location settings page when "Multi-location settings" is clicked.
	 *
	 * @return {void}
	 */
	private redirectToMultiLocationSettings(): void {
		this.closeNavAndSelector();
		this.$router.push('/multi-location-settings').catch(() => {});
	}

	/**
	 * Closes the mobile navbar and location selector and resets search string.
	 *
	 * @return {void}
	 */
	private closeNavAndSelector(): void {
		this.toggleMobileNav(false);
		this.selectorOpen = false;
		this.searchString = '';
	}
}

